import { TemplateResult } from 'lit-html';
import JrModal from '@libs/modal/index';
import { googleOneTapSignIn } from '@services/xhr-service';
import { getWhat, getWhere } from "@services/search-service";
import { getDomain } from '@services/email-service';
import { JtagService } from '@services/jtag-service';
import GoogleOneTapJppSecondStepTemplate from './templates';



enum InputType {
  WHAT = 'what',
  WHERE = 'where'
}

export default class GoogleOneTapJppSecondStepModal extends JrModal {
  private loading: boolean;
  private formData: IProposalFormData;
  private formSubmitted: boolean = false;
  private modalState: 'proposal' | 'welcome' | 'error' = 'proposal';

  constructor(
    readonly customModalClass: string,
    readonly wrapperClass: string,
    readonly idToken: string,
    readonly jrConfigGlobal: JrConfigGlobal
  ) {
    super(customModalClass, wrapperClass, jrConfigGlobal);
    this.loading = false;
    this.formData = this.getFormData();
  }

  getContent = async (): Promise<TemplateResult> => GoogleOneTapJppSecondStepTemplate({ messages: this.jrConfigGlobal.messages, feature: this.jrConfigGlobal.feature } as ProposalConfigs, this.getModel());

  openedCallback = (): void => { }

  handleSubmit = async (event: Event) => {
    event.preventDefault();

    const { what, where }: IProposalModalFormElements = <IProposalModalFormElements>(<HTMLFormElement>event.target);
    this.setWhat(what?.value || '');
    this.setWhere(where?.value || '');
    this.setFormSubmitted(true);

    this.render();

    if (this.isValidform()) {
      await this.signIn()
    }
  }

  handleClearWhatInput = (): void => this.setDOMElement('', InputType.WHAT);

  handleWhereInput = (value: string): void => this.setDOMElement(value, InputType.WHERE);

  handleWhatInput = (event: Event): void => {
    const eventValue = (<HTMLInputElement>event.target).value.trim();

    if (eventValue) {
      this.setDOMElement(eventValue, InputType.WHAT);
    }
  }

  private getModel = (): IProposalModel => ({
    handleSubmit: this.handleSubmit,
    handleWhatInput: this.handleWhatInput,
    handleWhereInput: this.handleWhereInput,
    handleClearWhatInput: this.handleClearWhatInput,
    close: this.close,
    loading: this.loading,
    formData: this.formData,
    formSubmitted: this.formSubmitted,
    modalState: this.modalState,
  });

  private getFormData = (jrConfigGlobal: JrConfigGlobal = window.jrConfigGlobal): IProposalFormData => {
    const {
      search: {
        where: { resolvedByIp }
      }
    } = jrConfigGlobal;

    return {
      what: {
        value: getWhat(jrConfigGlobal),
      },
      where: {
        value: resolvedByIp || getWhere(jrConfigGlobal),
        isResolvedByIp: resolvedByIp.length > 0
      }
    }
  }

  private setLoading = (loading: boolean): void => {
    this.loading = loading;
  }

  private setFormSubmitted = (isSubmitted: boolean): void => {
    this.formSubmitted = isSubmitted;
  }

  private setWhat = (eventValue: string): void => {
    this.formData.what.value = eventValue;
  }

  private setWhere = (eventValue: string): void => {
    this.formData.where.value = eventValue;
  }

  private setDOMElement = (value: string, inputType: string): void => {
    if (inputType === InputType.WHAT) {
      this.setWhat(value);
    }
    if (inputType === InputType.WHERE) {
      this.setWhere(value);
    }

    this.render();
  }

  private isValidform = (): boolean => this.formData.what.value.length > 0 || this.formData.where.value.length > 0;

  private signIn = async () => {
    this.setLoading(true);
    this.render();

    const signInPayload: IGoogleOneTapSignIn = {
      idToken: this.idToken,
      page: 'JPP',
      statement: {
        what: this.formData.what.value,
        where: this.formData.where.value,
        radius: undefined,
      },
      advertId: this.jrConfigGlobal.jobPreviewPage?.advertId || '',
      timeout: this.jrConfigGlobal.googleOneTap.timeout,
    }

    try {
      const signInResponse: Response = await googleOneTapSignIn(signInPayload);
      this.setLoading(false);
      this.render();

      if (signInResponse.ok) {
        const { email } = await signInResponse.json();
        this.afterSignIn(email)
      } else {
        this.modalState = 'error';
        this.render();
      }
    } catch (_) {
      this.setLoading(false);
      this.modalState = 'error';
      this.render();
    }
  }

  private afterSignIn = (userEmail: string): void => {
    JtagService.trackSubscription('google_one_tap', getDomain(userEmail) || '');
    JtagService.trackCustomEvent('google_one_tap_login');

    this.modalState = 'welcome';
    this.render();
  }
}